import * as React from "react";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { CopySimpleIcon } from "@phosphor-icons/react/dist/ssr/CopySimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";

import type { CloneableRequest } from "@/hooks/api/use-campaign-requests";
import {
	useCampaignRequestOutcomes,
	useCloneableCampaignRequests,
	useCreateCampaignRequestOutcome,
	useDeleteCampaignRequestOutcome,
	useStarterCampaignRequestOutcomes,
	useUpdateCampaignRequestNotification,
	useUpdateCampaignRequestOutcome,
} from "@/hooks/api/use-campaign-requests";

import { DEFAULT_NOTIFICATION_TEMPLATES, isFrozen, OUTCOME_SOURCE_LABELS, SYSTEM_DISPOSITION_LINE } from "../constants";
import { labelOf } from "../humanise";
import { CaptureFieldsEditor } from "./capture-fields-editor";
import type { NotificationDraft } from "./notification-editor";
import { NotificationEditor } from "./notification-editor";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 5 — outcomes.
 *
 * Three jobs in one place because they feed each other: what we pull out of each call, the outcomes
 * we record, and who gets emailed when one of them is a win.
 *
 * Three tabs, not three disclosures. They were numbered sections that each had to state what they
 * held while they were shut — which is a summary of a table sitting one click away, written in
 * prose, kept in step with the thing it describes by hand. The jobs are distinct and are done in
 * any order, which is what a tab is for. Nothing is hidden that the canvas asks for; the line that
 * used to introduce each section is the line under its tab.
 *
 * Every panel stays mounted. The notification preview decrypts the client's lead list on the
 * server and writes a row into that document's access trail, and a panel that unmounted would
 * spend one of those on every switch between tabs.
 *
 * Nobody starts from a blank list. Three sources stack — the standard set for the campaign type,
 * whatever can be read out of the uploaded call flow, and a one-click load from a previous
 * campaign — so the client edits rather than authors.
 *
 * Each outcome carries two independent switches, not one. "Counts as a conversion" drives the
 * stats and the results page; "Email" drives the notification. They usually overlap, but a referral
 * can be worth an email without counting as a win.
 */

type OutcomesTab = "capture" | "outcomes" | "email";

interface TabPanelProps {
	tab: OutcomesTab;
	active: OutcomesTab;
	/** The line that used to sit under the section heading. One sentence, always. */
	help: React.ReactNode;
	children: React.ReactNode;
}

function TabPanel({ tab, active, help, children }: TabPanelProps): React.JSX.Element {
	return (
		<Box
			role="tabpanel"
			id={`outcomes-panel-${tab}`}
			aria-labelledby={`outcomes-tab-${tab}`}
			hidden={tab !== active}
			// Hidden rather than unmounted — see the note at the top of the file.
			sx={{ display: tab === active ? "block" : "none" }}
		>
			<Stack spacing={2}>
				<Typography variant="body2" color="text.secondary">
					{help}
				</Typography>
				{children}
			</Stack>
		</Box>
	);
}

interface LoadFromPreviousDialogProps {
	requestId: string;
	open: boolean;
	onClose: () => void;
	/** The campaigns worth offering. Never empty — the button that opens this is not shown then. */
	options: CloneableRequest[];
	/** Names already on this request. Loading never duplicates what is already here. */
	existingNames: string[];
}

/**
 * Reads another request's outcome list and writes the ones this request does not already have.
 *
 * System rows are skipped: they are the platform's own dispositions and every request already has
 * them. Names are matched case-insensitively because a disposition renamed by case alone splits
 * the rollup as surely as a rename does.
 *
 * One previous campaign is not a choice. The dropdown is only drawn where there is something to
 * choose between; with a single campaign behind them the dialog names it and offers the rows.
 */
function LoadFromPreviousDialog({
	requestId,
	open,
	onClose,
	options,
	existingNames,
}: LoadFromPreviousDialogProps): React.JSX.Element {
	const only = options.length === 1 ? options[0] : null;

	const [picked, setPicked] = React.useState("");
	const [busy, setBusy] = React.useState(false);

	const sourceId = only?.id ?? picked;
	const { data: sourceOutcomes, isLoading } = useCampaignRequestOutcomes(open && sourceId ? sourceId : null);
	const createMutation = useCreateCampaignRequestOutcome();

	const taken = new Set(existingNames.map((name) => name.toLowerCase()));
	const importable = (sourceOutcomes ?? []).filter(
		(outcome) => !outcome.isSystem && !taken.has(outcome.name.toLowerCase())
	);

	const handleLoad = async (): Promise<void> => {
		setBusy(true);

		try {
			for (const outcome of importable) {
				await createMutation.mutateAsync({
					requestId,
					data: {
						name: outcome.name,
						description: outcome.description,
						isConversion: outcome.isConversion,
						notifyOnMatch: outcome.notifyOnMatch,
					},
				});
			}
			onClose();
		} catch {
			// Reported by the hook.
		} finally {
			setBusy(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{only ? `Load the outcomes from ${only.title}` : "Load from a previous campaign"}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{only ? null : (
						<TextField
							select
							fullWidth
							label="Which campaign?"
							value={picked}
							onChange={(event) => setPicked(event.target.value)}
						>
							{options.map((option) => (
								<MenuItem key={option.id} value={option.id}>
									{option.title}
								</MenuItem>
							))}
						</TextField>
					)}

					{isLoading ? <Typography variant="body2">Reading its outcomes…</Typography> : null}

					{sourceId && !isLoading ? (
						<Box>
							<Typography variant="body2" color="text.secondary">
								{importable.length === 0
									? "Nothing new to bring across — this request already has every outcome that one had."
									: `${importable.length} would be added:`}
							</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
								{importable.map((outcome) => (
									<Chip key={outcome.id} label={outcome.name} size="small" variant="outlined" />
								))}
							</Box>
						</Box>
					) : null}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button color="inherit" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="contained" disabled={busy || importable.length === 0} onClick={() => void handleLoad()}>
					Add {importable.length === 0 ? "" : importable.length} outcome{importable.length === 1 ? "" : "s"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export function StepOutcomes({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const starterMutation = useStarterCampaignRequestOutcomes();
	const createMutation = useCreateCampaignRequestOutcome();
	const updateMutation = useUpdateCampaignRequestOutcome();
	const deleteMutation = useDeleteCampaignRequestOutcome();
	const notificationMutation = useUpdateCampaignRequestNotification();

	const [name, setName] = React.useState("");
	const [description, setDescription] = React.useState("");
	const [loadOpen, setLoadOpen] = React.useState(false);
	const [busy, setBusy] = React.useState(false);
	/** Raised by the email panel: contacts with no recipient, and nowhere for their win to go. */
	const [recipientGap, setRecipientGap] = React.useState(false);

	/**
	 * Which job they land on.
	 *
	 * The outcome switches are the decision this step exists to take, so that is where it opens —
	 * unless nothing has been captured yet, in which case the first job is genuinely unanswered
	 * and every other tab is one click away.
	 */
	const [tab, setTab] = React.useState<OutcomesTab>(request.captureFields.length === 0 ? "capture" : "outcomes");

	/**
	 * The three templates arrive filled in (UX §08), not as placeholder text.
	 *
	 * A placeholder is not a default: an unfilled To sends nothing, nothing visibly fails when it
	 * doesn't, and the review checklist then correctly reports "nobody is told" for a client who
	 * simply never typed a brace. Only a notification that has never been saved takes the defaults
	 * — a row that exists is the client's own answer, including where they deliberately cleared a
	 * field.
	 *
	 * `bodyTemplate` has no default and never gets one: empty means "the standard email", which is
	 * what the API resolves and what the editor shows.
	 */
	const [notification, setNotification] = React.useState<NotificationDraft>({
		toTemplate:
			request.notification?.toTemplate ?? (request.notification ? "" : DEFAULT_NOTIFICATION_TEMPLATES.toTemplate),
		bccTemplate:
			request.notification?.bccTemplate ?? (request.notification ? "" : DEFAULT_NOTIFICATION_TEMPLATES.bccTemplate),
		subjectTemplate:
			request.notification?.subjectTemplate ??
			(request.notification ? "" : DEFAULT_NOTIFICATION_TEMPLATES.subjectTemplate),
		clientNote: request.notification?.clientNote ?? "",
		bodyTemplate: request.notification?.bodyTemplate ?? "",
		fallbackEmail: request.notification?.fallbackEmail ?? "",
		excludeContactsWithoutRecipient: request.notification?.excludeContactsWithoutRecipient ?? false,
	});

	const editable = request.outcomes.filter((outcome) => !outcome.isSystem);
	const conversionCount = editable.filter((outcome) => outcome.isConversion).length;
	const notifyCount = editable.filter((outcome) => outcome.notifyOnMatch).length;

	/**
	 * Pre-fill on first arrival — UX §08: "Pre-filled for you — standard outcomes for this campaign
	 * type, plus anything we spotted in your call flow." Note 1: three sources stack so the client
	 * edits rather than authors, and a step that opens empty with two buttons on it is the client
	 * authoring.
	 *
	 * Omitting `source` asks for both, in order. The API seeds and never resets — the standard set
	 * only lands on an empty list, so a row the client deleted does not come back — and the ref
	 * guard stops a second request while the first is in flight, since the list stays empty until
	 * it resolves.
	 */
	const seeded = React.useRef(false);
	const editableRequest = !isFrozen(request.state);

	React.useEffect(() => {
		// Not on a settled request: `POST /outcomes/starter` needs `updateSettled` once the terms
		// are frozen, which is ours alone, so firing it for a client would only ever toast a 403.
		if (seeded.current || editable.length > 0 || !editableRequest) return;
		seeded.current = true;
		starterMutation.mutate({ requestId: request.id });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- the mutation object is new each render.
	}, [request.id, editable.length, editableRequest]);

	/**
	 * The previous campaigns worth offering, asked for here rather than inside the dialog.
	 *
	 * A button that opens a dialog holding an empty dropdown is the first campaign an account ever
	 * runs, every time. The answer decides whether the button exists at all.
	 */
	const { data: cloneable } = useCloneableCampaignRequests(!readOnly && editableRequest);
	const previousCampaigns = (cloneable ?? []).filter((option) => option.id !== request.id);

	/** The two re-seed buttons only mean something against something we can actually read. */
	const hasCallFlow = request.documents.some(
		(document) => document.kind === "call_flow" || document.kind === "revised_call_flow"
	);

	const handleAdd = async (): Promise<void> => {
		if (name.trim() === "") return;

		try {
			await createMutation.mutateAsync({
				requestId: request.id,
				data: { name: name.trim(), description: description.trim() },
			});
			setName("");
			setDescription("");
		} catch {
			// Reported by the hook.
		}
	};

	const payload = {
		toTemplate: notification.toTemplate.trim() || null,
		bccTemplate: notification.bccTemplate.trim() || null,
		subjectTemplate: notification.subjectTemplate.trim() || null,
		clientNote: notification.clientNote.trim() || null,
		// Saved exactly as it was written — the `{{variable}}` string the summary processor
		// resolves, braces and all. Null is not "no body": it is the standard one.
		bodyTemplate: notification.bodyTemplate.trim() === "" ? null : notification.bodyTemplate,
		fallbackEmail: notification.fallbackEmail.trim() || null,
		excludeContactsWithoutRecipient: notification.excludeContactsWithoutRecipient,
	};

	/**
	 * Whether the notification on screen differs from the row behind it.
	 *
	 * Unchanged means nothing is written: the notification PUT comes back through `recompute()` on
	 * the API, which withdraws a live quote and sends the request back for re-pricing. See
	 * `./dirty`. A request that has never had a notification is always dirty, because the defaults
	 * this screen shows are only real once they have been saved.
	 *
	 * The outcome rows are not in this: each one is written by its own button as it is added,
	 * renamed or deleted, so there is never an unsaved outcome to lose.
	 */
	const saved = request.notification;
	const dirty = !(
		saved !== null &&
		saved !== undefined &&
		saved.toTemplate === payload.toTemplate &&
		saved.bccTemplate === payload.bccTemplate &&
		saved.subjectTemplate === payload.subjectTemplate &&
		saved.clientNote === payload.clientNote &&
		saved.bodyTemplate === payload.bodyTemplate &&
		saved.fallbackEmail === payload.fallbackEmail &&
		saved.excludeContactsWithoutRecipient === payload.excludeContactsWithoutRecipient
	);

	// Only worth asking about once they have actually moved something. A request whose notification
	// has never been saved is "dirty" from the moment the step opens, and prompting on the way out
	// of a screen nobody touched is how a guard gets clicked through without being read.
	useWizardDirty(dirty && saved !== null && saved !== undefined);

	const save = async (): Promise<boolean> => {
		if (!dirty) return true;

		setBusy(true);

		try {
			await notificationMutation.mutateAsync({ requestId: request.id, data: payload });
			return true;
		} catch {
			return false;
		} finally {
			setBusy(false);
		}
	};

	const handleContinue = async (): Promise<void> => {
		if (await save()) goTo(6);
	};

	const handleSaveExit = async (): Promise<void> => {
		if (await save()) exit();
	};

	return (
		<Stack spacing={2}>
			<StepHeading
				title="What to capture, what counts as a win, who hears about it"
				help="Three jobs in one place, because they feed each other. What to ask is already in your call flow — we don't ask for it twice."
			/>

			<Box sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
				<Tabs value={tab} variant="scrollable" onChange={(_event, value: OutcomesTab) => setTab(value)}>
					<Tab
						label="What we capture"
						value="capture"
						id="outcomes-tab-capture"
						aria-controls="outcomes-panel-capture"
						sx={{ minHeight: "auto" }}
					/>
					<Tab
						label="Outcomes"
						value="outcomes"
						id="outcomes-tab-outcomes"
						aria-controls="outcomes-panel-outcomes"
						sx={{ minHeight: "auto" }}
					/>
					<Tab
						label="Email"
						value="email"
						id="outcomes-tab-email"
						aria-controls="outcomes-panel-email"
						// The one failure on this step that costs the client conversions is on the
						// panel they may never open. It travels to the tab.
						icon={recipientGap ? <WarningIcon color="var(--mui-palette-warning-main)" /> : undefined}
						iconPosition="end"
						sx={{ minHeight: "auto" }}
					/>
				</Tabs>
			</Box>

			<TabPanel
				tab="capture"
				active={tab}
				help="Give each one a title and say what it is. We pull it out of the conversation automatically — no extra work on the call."
			>
				<CaptureFieldsEditor requestId={request.id} fields={request.captureFields} readOnly={readOnly} />
			</TabPanel>

			<TabPanel
				tab="outcomes"
				active={tab}
				help="Pre-filled for you — standard outcomes for this campaign type, plus anything we spotted in your call flow."
			>
				{/* Only when there is nothing to look at. A list that is on the screen describes
				    itself — every row carries the pill saying where it came from. */}
				{starterMutation.isPending && editable.length === 0 ? (
					<Typography variant="caption" color="text.secondary">
						Putting the standard set together for you, and reading your call flow…
					</Typography>
				) : editable.length === 0 ? (
					<Typography variant="caption" color="text.secondary">
						Nothing came back from the standard set or your call flow. Add what matters for this campaign.
					</Typography>
				) : null}

				{readOnly ? null : (
					<Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
						{previousCampaigns.length === 0 ? null : (
							<Button size="small" variant="outlined" startIcon={<CopySimpleIcon />} onClick={() => setLoadOpen(true)}>
								Load from a previous campaign
							</Button>
						)}
						{/* The two re-seed actions are recovery, not a choice to make on arrival:
						    the list is already seeded by the time this renders. */}
						<Button
							size="small"
							color="inherit"
							disabled={starterMutation.isPending}
							onClick={() => starterMutation.mutate({ requestId: request.id, source: "starter" })}
						>
							Load the Standard Set
						</Button>
						{hasCallFlow ? (
							<Button
								size="small"
								color="inherit"
								disabled={starterMutation.isPending}
								onClick={() => starterMutation.mutate({ requestId: request.id, source: "call_flow" })}
							>
								Read My Call Flow
							</Button>
						) : null}
					</Stack>
				)}

				<Box sx={{ overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Outcome</TableCell>
								<TableCell sx={{ fontWeight: 600, minWidth: 280 }}>What It Means</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="center">
									Counts As Conversion
								</TableCell>
								<TableCell sx={{ fontWeight: 600 }} align="center">
									Email
								</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{editable.map((outcome) => (
								<TableRow key={outcome.id}>
									<TableCell>
										<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{outcome.name}
											</Typography>
											{/* Where the row came from, one row at a time — the paragraph that
											    used to say the same thing for all of them is gone. */}
											<Chip label={labelOf(OUTCOME_SOURCE_LABELS, outcome.source)} size="small" variant="outlined" />
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="body2" color="text.secondary">
											{outcome.description || "—"}
										</Typography>
									</TableCell>
									<TableCell align="center">
										{/* Drives the stats and the results page. */}
										<Switch
											disabled={readOnly}
											inputProps={{ "aria-label": `Count ${outcome.name} as a conversion` }}
											checked={outcome.isConversion}
											onChange={(event) =>
												updateMutation.mutate({
													requestId: request.id,
													outcomeId: outcome.id,
													data: { isConversion: event.target.checked },
												})
											}
										/>
									</TableCell>
									<TableCell align="center">
										{/* Independent of the switch beside it: an email is not a win. */}
										<Switch
											disabled={readOnly}
											inputProps={{ "aria-label": `Email someone when ${outcome.name} is matched` }}
											checked={outcome.notifyOnMatch}
											onChange={(event) =>
												updateMutation.mutate({
													requestId: request.id,
													outcomeId: outcome.id,
													data: { notifyOnMatch: event.target.checked },
												})
											}
										/>
									</TableCell>
									<TableCell align="right">
										{readOnly ? null : (
											<Tooltip title="Remove">
												<span>
													<IconButton
														aria-label={`Remove ${outcome.name}`}
														color="error"
														size="small"
														disabled={deleteMutation.isPending}
														onClick={() => deleteMutation.mutate({ requestId: request.id, outcomeId: outcome.id })}
													>
														<TrashIcon />
													</IconButton>
												</span>
											</Tooltip>
										)}
									</TableCell>
								</TableRow>
							))}

							{/* One uneditable line, built from the platform's own dispositions rather than
							    from `request.outcomes` — those copies were deleted, deliberately, because a
							    client-editable definition of a row the dialer owns splits the rollup. */}
							<TableRow>
								{/* No "system" pill: it is our word for ourselves, and the cell beside it
								    already reads "Recorded automatically — can't be edited". */}
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{SYSTEM_DISPOSITION_LINE}
									</Typography>
								</TableCell>
								<TableCell colSpan={4}>
									<Typography variant="body2" color="text.secondary">
										{"Recorded automatically — can't be edited."}
									</Typography>
								</TableCell>
							</TableRow>

							{readOnly ? null : (
								<TableRow>
									<TableCell>
										<TextField
											fullWidth
											size="small"
											placeholder="Booked a demo"
											value={name}
											onChange={(event) => setName(event.target.value)}
										/>
									</TableCell>
									<TableCell>
										<TextField
											fullWidth
											size="small"
											placeholder="Agreed to a scheduled product demonstration"
											value={description}
											onChange={(event) => setDescription(event.target.value)}
										/>
									</TableCell>
									<TableCell colSpan={3}>
										<Button
											size="small"
											startIcon={<PlusIcon />}
											disabled={name.trim() === "" || createMutation.isPending}
											onClick={() => void handleAdd()}
										>
											Add an Outcome
										</Button>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Box>
			</TabPanel>

			<TabPanel
				tab="email"
				active={tab}
				help={
					notifyCount === 0
						? "Nothing is emailed until an outcome has “Email” switched on."
						: `Emailed as soon as ${notifyCount === 1 ? "the outcome you picked is" : `one of the ${notifyCount} outcomes you picked is`} recorded. To takes one address; everyone after it is copied in.`
				}
			>
				{notifyCount === 0 && !readOnly ? (
					<Box>
						<Button size="small" variant="outlined" onClick={() => setTab("outcomes")}>
							Choose which outcomes send an email
						</Button>
					</Box>
				) : null}

				<NotificationEditor
					value={notification}
					onChange={setNotification}
					requestId={request.id}
					captureFields={request.captureFields}
					campaignTitle={request.title}
					hasLeadList={request.documents.some((document) => document.kind === "lead_list")}
					onRecipientGap={setRecipientGap}
					readOnly={readOnly}
				/>
			</TabPanel>

			{previousCampaigns.length === 0 ? null : (
				<LoadFromPreviousDialog
					requestId={request.id}
					open={loadOpen}
					onClose={() => setLoadOpen(false)}
					options={previousCampaigns}
					existingNames={request.outcomes.map((outcome) => outcome.name)}
				/>
			)}

			<WizardFooter
				busy={busy}
				onBack={() => goTo(4)}
				onSaveExit={readOnly ? undefined : () => void handleSaveExit()}
				onContinue={readOnly ? () => goTo(6) : () => void handleContinue()}
				// The two switch columns, rolled up where they can be read from any of the three
				// tabs — including the one where the client is deciding who hears about a win.
				leading={
					editable.length === 0 ? undefined : (
						<Typography variant="caption" color="text.secondary">
							{`${conversionCount} of ${editable.length} count as a conversion · ${notifyCount} send an email`}
						</Typography>
					)
				}
			/>
		</Stack>
	);
}
